import type { OwnedDbOperationResult } from "@9amhealth/shared";
import { runForOwner } from "./DbService";
import type { CachedTask } from "./dbTypes";

export interface CachedProgramTasks {
  tasks: CachedTask[];
  lastFetchAt: number | null;
}

export const DbTaskService = {
  async getProgramCacheForOwner(
    expectedOwnerId: string | null,
    program: string
  ): Promise<OwnedDbOperationResult<CachedProgramTasks>> {
    return runForOwner(expectedOwnerId, (db) =>
      db.transaction("r", db.tasks, db.meta, async () => {
        const [tasks, meta] = await Promise.all([
          db.tasks.where("program").equals(program).toArray(),
          db.meta.get(`tasks:${program}`)
        ]);
        return { tasks, lastFetchAt: meta?.lastFetchAt ?? null };
      })
    );
  },

  async replaceProgramCacheForOwner(
    expectedOwnerId: string | null,
    program: string,
    tasks: CachedTask[],
    lastFetchAt?: number
  ): Promise<OwnedDbOperationResult<void>> {
    return runForOwner(expectedOwnerId, (db) =>
      db.transaction("rw", db.tasks, db.meta, async () => {
        await db.tasks.where("program").equals(program).delete();
        if (tasks.length > 0) {
          await db.tasks.bulkPut(tasks);
        }
        if (lastFetchAt !== undefined) {
          await db.meta.put({ key: `tasks:${program}`, lastFetchAt });
        }
      })
    );
  },

  async clearTaskCacheForOwner(
    expectedOwnerId: string | null
  ): Promise<OwnedDbOperationResult<void>> {
    return runForOwner(expectedOwnerId, (db) =>
      db.transaction("rw", db.tasks, db.meta, async () => {
        await db.tasks.clear();
        const taskMetaKeys = (await db.meta.toArray())
          .filter(({ key }) => key.startsWith("tasks:"))
          .map(({ key }) => key);
        if (taskMetaKeys.length > 0) {
          await db.meta.bulkDelete(taskMetaKeys);
        }
      })
    );
  }
};
